import { TF } from 'react-translator-component';

import { countdownFrom } from './tokenCountdown';

import {
  InviteDirection,
  InviteListItem,
  InviteStatus,
} from '../../../../types/responses/Interoperability';

/**
 * Rótulos e cores da lista de convites.
 *
 * Usa TF e não T onde o retorno é declarado string: o T devolve o array de nós do
 * react-html-parser, que mente no tipo e estoura na primeira operação de string.
 */

const STATUS_LABELS: Record<InviteStatus, string> = {
  // VALID é o estado do registro; na lista o que interessa é que ele aguarda.
  // "Válido" descreveria o token, não a situação do convite.
  VALID: 'Pending',
  USED: 'Accepted',
  EXPIRED: 'Expired',
  REVOKED: 'Revoked',
};

const DIRECTION_LABELS: Record<InviteDirection, string> = {
  SENT: 'Sent',
  RECEIVED: 'Received',
};

export const inviteStatusLabel = (status: InviteStatus): string =>
  TF(STATUS_LABELS[status] || status);

export const inviteDirectionLabel = (direction: InviteDirection): string =>
  TF(DIRECTION_LABELS[direction] || direction);

/**
 * Cor por estado, no mesmo código do resto do módulo.
 *
 * Aceito e revogado precisam de cores distantes: um virou contrato, o outro foi
 * cancelado, e lista se lê de relance.
 */
export const INVITE_STATUS_STYLE: Record<InviteStatus, { dot: string; fg: string; bg: string }> = {
  VALID: { dot: '#e0700f', fg: '#a8500f', bg: '#fdeee4' },
  USED: { dot: '#26a35c', fg: '#1e7a45', bg: '#e7f6ed' },
  EXPIRED: { dot: '#9aa3af', fg: '#6b7280', bg: '#eef0f2' },
  REVOKED: { dot: '#c8352f', fg: '#b02525', bg: '#fdecec' },
};

const shortDate = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const moment = new Date(value);

  if (Number.isNaN(moment.getTime())) {
    return '';
  }

  const pad = (part: number): string => String(part).padStart(2, '0');

  return `${pad(moment.getDate())}/${pad(moment.getMonth() + 1)}`;
};

/**
 * Texto da coluna de prazo.
 *
 * O mesmo campo significa coisas diferentes conforme o estado: quanto falta no
 * pendente, quando foi aceito no usado, quando venceu no expirado. Revogado não tem
 * data porque a tabela não guarda `revoked_at` — a data existe na trilha, como
 * evento, e inventá-la aqui seria chute.
 */
export const inviteDeadline = (invite: InviteListItem, now: Date = new Date()): string => {
  if (invite.status === 'USED') {
    const when = shortDate(invite.used_at);

    return when ? `${TF('accepted on')} ${when}` : TF('accepted');
  }

  if (invite.status === 'REVOKED') {
    return TF('revoked');
  }

  if (invite.status === 'EXPIRED') {
    const when = shortDate(invite.expires_at);

    return when ? `${TF('expired on')} ${when}` : TF('expired');
  }

  if (!invite.expires_at) {
    return '—';
  }

  const remaining = countdownFrom(invite.expires_at, now);

  return remaining.expired ? TF('expired') : `${TF('expires in')} ${remaining.text}`;
};

/**
 * Se a linha oferece revogar.
 *
 * Só no que eu enviei e ainda aguarda. No recebido o servidor recusa com 403 —
 * revogar é ato de quem convidou, e quem não quer aceitar simplesmente não aceita.
 * Oferecer o botão ali seria oferecer um erro.
 */
export const canRevoke = (invite: InviteListItem): boolean =>
  invite.direction === 'SENT' && invite.status === 'VALID';

/**
 * Se oferece excluir de vez: só no que eu enviei e já está revogado. No recebido o servidor
 * recusa com 403 (excluir é ato de quem convidou), e em não-revogado com 400 — oferecer o botão
 * ali seria oferecer um erro, exatamente o 403 "Só quem enviou o convite pode excluí-lo".
 */
export const canDelete = (invite: InviteListItem): boolean =>
  invite.direction === 'SENT' && invite.status === 'REVOKED';

/** Se a linha oferece revisar: convite recebido que ainda pode ser aceito. */
export const canReview = (invite: InviteListItem): boolean =>
  invite.direction === 'RECEIVED' && invite.status === 'VALID';

/**
 * Texto da linha de autor no detalhe do convite (MV2-1819).
 *
 * O travessão que estava aqui dizia três coisas ao mesmo tempo: "ninguém agiu", "o sistema
 * não guardou" e "existe, mas é de outra empresa e você não vê". Foi lido como defeito, e
 * com razão — o único jeito de saber qual das três era abrir o código.
 *
 * `mine` é se o lado que agiu é o meu: quem gerou, no convite enviado; quem aceitou, no
 * recebido. Do outro lado o servidor omite de propósito — é funcionário de outra empresa — e
 * a tela precisa dizer isso, e não fingir que o dado sumiu.
 */
export const authorLabel = (value: string | null | undefined, mine: boolean): string => {
  if (!mine) {
    return TF('not shown (other company)');
  }

  const author = (value || '').trim();

  // Convite gerado antes de o autor passar a ser guardado (MV2-1807), ou por integração via
  // API sem identificação. O dado não existe: dizer isso é melhor do que um traço.
  return author || TF('not recorded');
};
